// productScope.js
// Centralizes which product "applies_to" scopes a role may see/select in the
// Configuration screens, driven by the role's Available Features.
//
//   applies_to value  <-  feature that grants it
//     'windows'           ALLOW_WINDOWS
//     'patiodoors'        ALLOW_PATIO_DOORS
//
// A role restricted to windows (only ALLOW_WINDOWS) sees just "Windows" in the
// Add/Edit dropdowns and only windows-relevant rows in the grids. To add a
// product later, add one entry to PRODUCT_SCOPES — nothing else changes.

import { useFeatures } from './FeaturesContext';

export const PRODUCT_SCOPES = [
  { value: 'windows',    label: 'Windows',     feature: 'ALLOW_WINDOWS' },
  { value: 'patiodoors', label: 'Patio Doors', feature: 'ALLOW_PATIO_DOORS' },
];

export const useProductScope = () => {
  const { hasFeature } = useFeatures();

  const allowed = PRODUCT_SCOPES.filter((p) => hasFeature(p.feature));
  const allowedValues = allowed.map((p) => p.value);
  const hasAllProducts = allowed.length === PRODUCT_SCOPES.length;

  // Dropdown options: the products this role may work with, plus "All" only when
  // the role has every product (otherwise "All" would re-introduce other products).
  const scopeOptions = [
    ...allowed.map((p) => ({ value: p.value, label: p.label })),
    ...(hasAllProducts ? [{ value: 'all', label: 'All' }] : []),
  ];

  // Default scope for a new record (first allowed product, falling back to windows).
  const defaultScope = allowedValues[0] || 'windows';

  // Default for a new record that preserves each screen's original fallback for
  // full-access roles, but pins restricted roles to their own product. E.g.
  // pickDefault('all') -> 'all' for superadmin, 'windows' for a windows-only role.
  const pickDefault = (fallback) => (hasAllProducts ? fallback : allowedValues[0] || fallback);

  // Grid row visibility (strict): a role with every product sees all rows;
  // a restricted role sees ONLY rows whose scope is one of its products —
  // "all"-scoped rows are hidden too (per requirement: windows means windows).
  const canSeeScope = (appliesTo) =>
    hasAllProducts || allowedValues.includes(appliesTo);

  // When editing a row whose scope isn't normally selectable for this role
  // (e.g. an existing "all" row), keep its current value in the dropdown so
  // saving doesn't silently narrow it.
  const optionsForValue = (currentValue) => {
    if (currentValue && !scopeOptions.some((o) => o.value === currentValue)) {
      const known = PRODUCT_SCOPES.find((p) => p.value === currentValue);
      const label = known ? known.label : currentValue === 'all' ? 'All' : currentValue;
      return [{ value: currentValue, label }, ...scopeOptions];
    }
    return scopeOptions;
  };

  return { scopeOptions, allowedValues, hasAllProducts, defaultScope, pickDefault, canSeeScope, optionsForValue };
};
